import { type Translated } from '@myre/common-i18n';
import { type Currency } from '@myre/app-am-generated/types/Currency';
import {
  getObjectEntries,
  getObjectKeys,
  type ScaleFactor,
} from '@myre/common-primitives';
import { TagType } from '@myre/common-tag';
import {
  type AmI18n,
  type SystemI18n,
  type UiI18n,
  type UiLibI18n,
} from '@myre/domain-i18n-wordings';
import {
  ActionGroupComponent,
  type ActionGroup,
} from '@myre/ui-primitive-action-group';
import {
  ActionsDropdownComponent,
  type TextItem,
} from '@myre/ui-primitive-actions-dropdown';
import { ButtonComponent, ButtonTheme } from '@myre/ui-primitive-components';
import {
  DropdownComponent,
  DropdownTemplate,
  TextboxComponent,
  type ISelectItem,
} from '@myre/ui-primitive-forms';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  type AbstractControl,
} from '@angular/forms';
import { isEqual, isNil } from 'lodash-es';
import { debounceTime } from 'rxjs';

import { WORDINGS } from '../../../myre-wordings-loader';

import { MyuiDropdownFactorComponent } from '../../dropdown-factor/dropdown-factor.component';

import { SEARCH_KEY, SEARCH_TARGET_KEY } from '../constants/search.constants';

import { FilterItemComponent } from './filter-item/filter-item.component';
import {
  isConfigurableFilter,
  isDateFilter,
  isSearchFilter,
  isSelectFilter,
  isSwitchFilter,
} from './helpers/filter.helper';
import {
  type Filter,
  type FilterValue,
} from './interfaces/filters/base.filter';
import {
  type SearchFilter,
  type SearchTarget,
} from './interfaces/filters/search.filter';
import {
  defaultPaginationOptions,
  type OrderOption,
  type PaginationOption,
} from './interfaces/pagination.interface';
import {
  type FilterValues,
  type View,
  type ViewSelector,
} from './interfaces/view.interface';

const DEBOUNCE_TIME = 100;

const ORDER_KEY = 'order';

const NO_DOUBLE_CLICK_GUARD = 0;

@Component({
  selector: 'filter-controls-panel',
  templateUrl: './filter-controls-panel.component.html',
  styleUrls: ['./filter-controls-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionGroupComponent,
    ActionsDropdownComponent,
    ButtonComponent,
    DropdownComponent,
    MyuiDropdownFactorComponent,
    FilterItemComponent,
    FormsModule,
    ReactiveFormsModule,
    TextboxComponent,
    DropdownTemplate,
    NgTemplateOutlet,
  ],
})
export class FilterControlsPanelComponent {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly _wordings = inject<
    AmI18n & SystemI18n & UiI18n & UiLibI18n
  >(WORDINGS);

  @Input({ required: true })
  public set filters(filters: (Filter<string> | SearchFilter)[]) {
    if (!filters?.length) {
      return;
    }

    this._filters.set(filters);

    const form = this._form();

    if (form) {
      this._reconcileControls(form, filters);
    } else {
      this._createForm(filters);
    }

    this._captureFormValues();
  }

  /**
   * Active filters from the store. When provided, the form values will be
   * synchronized with these values (useful for external navigation like global search).
   */
  public readonly activeFilters = input<Record<string, unknown> | null>(null);

  public readonly views = input<View<string, string, string>[] | null>(null);

  public readonly searchPlaceholder = input<Translated>(
    this._wordings._ui.search(),
  );

  /**
   * If defined, the order options are displayed in a dropdown
   */
  public readonly orderOptions = input<OrderOption<string>[] | null>(null);

  public readonly currency = input<Currency | null>(null);

  public readonly factor = input<ScaleFactor>(1 as ScaleFactor);

  public readonly factorChange = output<ScaleFactor>();

  // Mutable: the value feeds `myre-dropdown`'s list, typed as a mutable array.
  public readonly paginationOptions = input<PaginationOption[]>(
    defaultPaginationOptions(this._wordings),
  );

  public readonly numberItemsPerPage = input<number>(
    this.paginationOptions()[0].value,
  );

  public readonly currentView = input<string | null>(null);
  public readonly onViewChange = output<string | null>();

  /** `'dropdown'` also brings the filters toggle. */
  public readonly viewSelector = input<ViewSelector>('buttons');

  public readonly isLoading = input<boolean>(false);

  public readonly ignoredFilters = input<string[]>([]);

  /**
   * Fold the paging control into the options menu even when it would be the
   * only thing in there — a list whose mockup shows an icon button rather than
   * a page-size dropdown sitting next to the search box.
   */
  public readonly alwaysCombineOptions = input<boolean>(false);

  /**
   * Actions on the list itself, offered at the right end of the filters row. They come
   * with the filters, so a folded panel never offers to act on a state it hides.
   */
  public readonly filterActions = input<ActionGroup | null>(null);

  public readonly formValuesChanged = output<FilterValues<string>>();

  public readonly pagination = output<number>();

  protected readonly _form = signal<UntypedFormGroup | null>(null);

  /** Mirrors the form's value, which is no signal, so the filtering state derives from it. */
  private readonly _formValues = signal<Record<string, FilterValue | null>>({});

  protected readonly _filters = signal<(Filter<string> | SearchFilter)[]>([]);

  /** Whether the `searchTarget` control is registered in the current form. */
  protected readonly _hasSearchTarget = signal(false);

  protected readonly _searchTargets = computed<readonly SearchTarget[]>(
    () => this._filters().find(isSearchFilter)?.targets ?? [],
  );

  /** Mirrors the `searchTarget` control, which is not a signal, to label the tag. */
  protected readonly _selectedSearchTargetKey = signal<string | null>(null);

  /** Undefined while the filters carry no target, when no dropdown is shown. */
  protected readonly _selectedSearchTargetLabel = computed<
    Translated | undefined
  >(() => {
    const targets = this._searchTargets();
    const selectedKey = this._selectedSearchTargetKey();

    return (targets.find(target => target.key === selectedKey) ?? targets[0])
      ?.label;
  });

  protected readonly _searchTargetItems = computed<TextItem[]>(() =>
    this._searchTargets().map(target => ({
      label: target.label,
      onClick: () => this._form()?.get(SEARCH_TARGET_KEY)?.setValue(target.key),
    })),
  );

  protected readonly _itemsPerPage = signal(this.numberItemsPerPage());

  protected readonly _isViewDropdown = computed(
    () => this.viewSelector() === 'dropdown',
  );

  /** The consumer lists its own views first: a line sets the user's apart from them. */
  protected readonly _viewOptions = computed<ISelectItem<string>[]>(() =>
    (this.views() ?? []).map((view, index, views) => ({
      label: view.label,
      value: view.key,
      separatorBefore: !view.isSystem && views[index - 1]?.isSystem === true,
    })),
  );

  /** Session-scoped, on purpose: the filters fold where the user folded them and nowhere else. */
  private readonly _areFiltersVisible = signal(true);

  /** Whether the filter controls are on screen: always so, unless the toggle hides them. */
  public readonly areFiltersVisible = computed(
    () => !this._isViewDropdown() || this._areFiltersVisible(),
  );

  protected readonly _ButtonTheme = ButtonTheme;

  /**
   * Whether anything narrows the list, the population a view opens on included: what is
   * offered to clear is what a filter holds, not what the user themselves put there.
   */
  public readonly isFiltering = computed<boolean>(() => {
    // The form is dropped and rebuilt on a view change: what it held said what the view
    // being left showed, and nothing at all about the one being entered.
    if (!this._form()) {
      return false;
    }

    return getObjectEntries(this._formValues()).some(([key, value]) => {
      if (this.ignoredFilters().includes(key)) {
        return false;
      }

      const filter = this._filters().find(f => f.key === key);

      if (!filter) {
        return false;
      }

      // A date always carries a value: counting it would pin the panel to "filtering".
      if (isDateFilter(filter)) {
        return false;
      }

      if (typeof value === 'string') {
        return value.trim().length > 0;
      }

      if (typeof value === 'boolean') {
        return value;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return !isNil(value);
    });
  });

  public readonly hasOrder = computed(() => !!this.orderOptions()?.length);

  public readonly hasPagination = computed(
    () => !!this.paginationOptions()?.length,
  );

  public readonly hasCurrencyConfig = computed(() => !!this.currency());

  protected readonly _configFilters = computed(() =>
    this._filters().filter(
      (f): f is Filter<string> =>
        isConfigurableFilter(f) && !!f.configFilter && !f.hidden,
    ),
  );

  /** Filters pinned to the search line rather than wrapped into the filter tags row. */
  protected readonly _inlineFilters = computed(() =>
    this._filters().filter(
      (f): f is Filter<string> => isDateFilter(f) && !!f.inline && !f.hidden,
    ),
  );

  /** Filters shown as tags on their own row. */
  protected readonly _rowFilters = computed(() =>
    this._filters().filter(filter => !!this._displayFilter(filter)),
  );

  /**
   * The row holds the filter tags and, at its right end, the actions on the list itself.
   * Left empty, it would still push the page content down by its top margin, so it is
   * dropped rather than rendered blank.
   */
  protected readonly _isFilterRowVisible = computed<boolean>(
    () =>
      this.areFiltersVisible() &&
      (!!this._rowFilters().length || !!this.filterActions()),
  );

  public readonly hasConfigFilters = computed(
    () => this._configFilters().length > 0,
  );

  public readonly useCombinedDropdown = computed(
    () =>
      (this.hasOrder() && this.hasPagination()) ||
      (this.alwaysCombineOptions() && this.hasPagination()) ||
      this.hasCurrencyConfig() ||
      this.hasConfigFilters(),
  );

  constructor() {
    // Bound to the form's existence alone: gating this on a transient state such as
    // `isLoading` would resubscribe on every flip, and emit each change as many times.
    effect(() => {
      const form = this._form();

      if (!form) {
        return;
      }

      form.valueChanges
        .pipe(takeUntilDestroyed(this._destroyRef), debounceTime(DEBOUNCE_TIME))
        .subscribe(values => {
          this.formValuesChanged.emit(values);
          this._captureFormValues(values);
        });
    });

    effect(() => {
      if (this.isLoading()) {
        return;
      }

      const orderControl = this.orderControl();
      const defaultOrder = this.orderOptions()?.find(option => option.selected);

      if (orderControl?.value === null && defaultOrder) {
        orderControl.setValue(defaultOrder.value, { emitEvent: false });
      }
    });

    // Sync form values with activeFilters from store (for external navigation like global search)
    effect(() => {
      const activeFilters = this.activeFilters();
      const form = this._form();

      if (!activeFilters || !form) {
        return;
      }

      for (const [key, value] of getObjectEntries(activeFilters)) {
        const control = form.get(String(key));

        if (!control) {
          continue;
        }

        if (String(key) === SEARCH_TARGET_KEY) {
          this._syncSearchTargetControl(control, value);

          continue;
        }

        if (this._isBeingTypedIn(String(key))) {
          continue;
        }

        if (!isEqual(control.value, value)) {
          control.setValue(value, { emitEvent: false });
        }
      }

      this._captureFormValues();
    });
  }

  public selectView(viewKey: string | null): void {
    this._onViewChange(viewKey);
  }

  /**
   * Filter keys the user has explicitly interacted with (including clearing
   * one back to empty) — a dirty control stays dirty even at a falsy value,
   * so callers can tell "user cleared it" apart from "never touched".
   */
  public getDirtyKeys(): string[] {
    if (!this._form()) {
      return [];
    }

    return getObjectKeys(this._form().controls).filter(
      key => this._form().get(String(key))?.dirty,
    ) as string[];
  }

  /**
   * Clears the filters for good: a cleared control is marked dirty, so the population the
   * current view opens on is not written back over it the moment the form emits.
   */
  public resetFormValues(keys: string[] = []): void {
    if (this._form()) {
      let changed = false;

      if (!keys.length) {
        const values = this._form().value ?? {};
        keys = getObjectKeys(values) as string[];
      }

      for (const key of keys) {
        if (!this.ignoredFilters().includes(String(key))) {
          // Blanking a date would leave the list with no date to read at all.
          const filter = this._filters().find(
            f => f.key === key && !isDateFilter(f),
          );
          const control = this._form().get(String(key));

          if (filter && control) {
            changed = true;
            control.setValue(null, { emitEvent: false });
            control.markAsDirty();
          }
        }
      }

      if (changed) {
        this._form().updateValueAndValidity();
      }

      return;
    }

    this._captureFormValues();
  }

  protected _displayFilter(
    filter: Filter<string> | SearchFilter,
  ): Filter<string> {
    if (filter.type === 'search' || filter.hidden) {
      return null;
    }

    if (isConfigurableFilter(filter) && filter.configFilter) {
      return null;
    }

    if (isDateFilter(filter) && filter.inline) {
      return null;
    }

    return filter;
  }

  protected _isSwitchFilter(filter: Filter<string>): boolean {
    return isSwitchFilter(filter);
  }

  protected _onViewChange(viewKey: string | null): void {
    if (viewKey === this.currentView()) {
      return;
    }

    this._resetForm();

    this.onViewChange.emit(viewKey);
  }

  protected _toggleFilters(): void {
    this._areFiltersVisible.update(visible => !visible);
  }

  protected _onPaginationChange(page: number): void {
    this.pagination.emit(page);
    this._itemsPerPage.set(page);
  }

  protected updateFactor(factor: ScaleFactor): void {
    this.factorChange.emit(factor);
  }

  protected readonly orderControl = computed(() => {
    return this._form()?.get(this._ORDER_KEY) as FormControl;
  });

  private _createForm(filters: (Filter<string> | SearchFilter)[]): void {
    const form = new UntypedFormGroup({
      [ORDER_KEY]: new FormControl<string>(null),
    });

    for (const filter of filters) {
      form.addControl(filter.key, new FormControl(this._defaultValue(filter)));
    }

    this._addSearchTargetControl(form);

    this._form.set(form);
    this._initControlListeners();
    this._captureFormValues();
  }

  /**
   * A list does not declare all its filters at once: a right resolved asynchronously adds
   * one, and the template binds it by name the moment it is declared. Every emission puts
   * the controls back in step with the filters, the ones already there keeping the value
   * the user gave them and their dirty state.
   */
  private _reconcileControls(
    form: UntypedFormGroup,
    filters: (Filter<string> | SearchFilter)[],
  ): void {
    for (const filter of filters) {
      if (form.get(filter.key)) {
        continue;
      }

      const active = this.activeFilters()?.[filter.key];

      const control = new FormControl(active ?? this._defaultValue(filter));

      form.addControl(filter.key, control, { emitEvent: false });
      this._listenToControl(filter.key, control);
    }

    if (this._searchTargets().length && !form.get(SEARCH_TARGET_KEY)) {
      this._addSearchTargetControl(form);
    }

    const declared = new Set<string>(filters.map(filter => filter.key));

    for (const key of getObjectKeys(form.controls)) {
      const name = String(key);

      if (
        declared.has(name) ||
        name === ORDER_KEY ||
        name === SEARCH_TARGET_KEY
      ) {
        continue;
      }

      form.removeControl(name, { emitEvent: false });
    }
  }

  private _defaultValue(filter: Filter<string> | SearchFilter): FilterValue {
    if (isSelectFilter(filter)) {
      return filter.defaultSelectedOptions ?? null;
    }

    if (isDateFilter(filter) || isSwitchFilter(filter)) {
      return filter.defaultValue ?? null;
    }

    return null;
  }

  /**
   * The search target is a technical companion of `search`, not a filter: it has no
   * `Filter` entry, so it never counts as an active filter nor gets reset.
   */
  private _addSearchTargetControl(form: UntypedFormGroup): void {
    this._hasSearchTarget.set(!!this._searchTargets().length);

    if (!this._hasSearchTarget()) {
      return;
    }

    const resolved = this._resolveSearchTarget(
      this.activeFilters()?.[SEARCH_TARGET_KEY],
    );

    this._selectedSearchTargetKey.set(resolved);

    const control = new FormControl<string>(resolved);

    control.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value =>
        this._selectedSearchTargetKey.set(
          typeof value === 'string' ? value : null,
        ),
      );

    form.addControl(SEARCH_TARGET_KEY, control, { emitEvent: false });
  }

  /**
   * What comes back from the store is a snapshot of the form taken one emission debounce
   * ago. Writing it into the free-text search while the user is still typing drops every
   * character entered since — so as long as the focus is in here, the form wins.
   * An external navigation (global search) never lands while the box is being typed in.
   */
  private _isBeingTypedIn(key: string): boolean {
    return (
      key === SEARCH_KEY &&
      this._elementRef.nativeElement.contains(document.activeElement)
    );
  }

  /**
   * The correction of an unknown target is emitted, so the store and the URL stop
   * carrying a field nothing can search on.
   */
  private _syncSearchTargetControl(
    control: AbstractControl,
    storedValue: unknown,
  ): void {
    const resolved = this._resolveSearchTarget(storedValue);
    const isStoredKnown = storedValue === resolved;

    if (isStoredKnown && isEqual(control.value, resolved)) {
      return;
    }

    // `emitEvent: false` skips `valueChanges`, so the mirror is written here too.
    this._selectedSearchTargetKey.set(resolved);

    control.setValue(resolved, { emitEvent: !isStoredKnown });
  }

  /**
   * The target travels through the URL and can be anything; an unknown key falls
   * back to the default target rather than searching on a field that doesn't exist.
   */
  private _resolveSearchTarget(value: unknown): string | null {
    const targets = this._searchTargets();

    if (!targets.length) {
      return null;
    }

    return (targets.find(target => target.key === value) ?? targets[0]).key;
  }

  /**
   * Listen to the value changes of the form controls and call the onChange function of the filters when the value changes.
   * Subscriptions are automatically unsubscribed when the component is destroyed.
   */
  private _initControlListeners(): void {
    for (const [formKey, control] of getObjectEntries(this._form().controls)) {
      this._listenToControl(String(formKey), control);
    }
  }

  private _listenToControl(formKey: string, control: AbstractControl): void {
    control.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const filter = this._filters().find(f => f.key === formKey);

        if (
          !filter ||
          filter.type === 'search' ||
          typeof filter.onChange !== 'function'
        ) {
          return;
        }

        const result = filter.onChange(control.value);

        if (!Array.isArray(result)) {
          return;
        }

        for (const { key, values } of result) {
          this._form().get(key).setValue(values);
        }
      });
  }

  private _captureFormValues(
    values: Record<string, FilterValue | null> = this._form()?.value ?? {},
  ): void {
    this._formValues.set(values);
  }

  private _resetForm(): void {
    // Set form to null to trigger complete recreation when new filters arrive
    // This ensures a clean slate and prevents race conditions during view transitions
    this._form.set(null);
  }

  protected readonly _ORDER_KEY = ORDER_KEY;

  protected readonly _NO_DOUBLE_CLICK_GUARD = NO_DOUBLE_CLICK_GUARD;

  protected readonly _searchTargetTagType = TagType.Quinary;
}
